import { Fragment, useEffect, useState } from 'react';
import type { SiteContent } from '@risma/data-client';
import type { Lang, Reservation, ServiceId, Table, Zone } from '../data/types';
import { useDashboard, useDashboardActions } from '../data/context';
import { t } from '../i18n/t';
import { Icon } from '../components/Icon';
import { Pill } from '../components/Pill';
import { Tags } from '../components/Tags';
import { STATUS } from '../components/helpers';
import type { CalView } from '../shell/routes';
import {
  clockLabel,
  rangesForDay,
  serviceWindow,
  svcMinute,
  type ServiceWindow,
} from '../lib/grid';
import { romeNowMin, todayRome } from '../lib/dates';

// In modalità API (produzione) "oggi"/"adesso" sono reali; in mock restano
// ancorati alla giornata e all'ora delle fixture, così la demo locale resta viva.
const IS_API = !!import.meta.env.VITE_RISMA_API_URL?.trim();
const TODAY = IS_API ? todayRome() : '2026-06-12';
const NOW_MIN = IS_API ? romeNowMin() : 21 * 60 + 18; // mock: 21:18

interface CalendarViewProps {
  lang: Lang;
  view: CalView;
  onOpen: (r: Reservation) => void;
}

export function CalendarView({ lang, view, onOpen }: CalendarViewProps) {
  const { reservations: allReservations, services, zones, tables, day, serviceCfg } =
    useDashboard();
  const actions = useDashboardActions();
  const [svcId, setSvcId] = useState<ServiceId>('cena');

  // Orari di apertura veri (modificabili nel Sito web) — stessa fonte di
  // AvailabilityView. La griglia li segue; finché caricano si usa il fallback.
  const [siteHours, setSiteHours] = useState<SiteContent['hours'] | null>(null);
  useEffect(() => {
    let alive = true;
    actions
      .loadSite()
      .then((s) => {
        if (alive) setSiteHours(s.draft?.hours ?? s.published?.hours ?? null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // Solo al mount: `actions` cambia identità a ogni render, includerlo qui
    // rifarebbe la fetch in continuazione.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Solo il giorno selezionato dal date stepper. Le righe mock non hanno
  // startsAt (sono a giornata singola) e passano sempre.
  const dayReservations = allReservations.filter(
    (r) => !r.startsAt || r.startsAt.slice(0, 10) === day,
  );

  const all = dayReservations.filter((r) => r.service === svcId);
  const live = all.filter((r) => r.status !== 'cancel');
  const covers = live.reduce((sum, r) => sum + r.covers, 0);

  // Finestra oraria per ogni servizio: dagli orari di apertura del giorno,
  // allungata per contenere ogni prenotazione (anche dopo mezzanotte).
  const ranges = rangesForDay(siteHours, day);
  const windows = Object.fromEntries(
    services.map((s) => {
      const list = dayReservations.filter((r) => r.service === s.id);
      return [
        s.id,
        serviceWindow({
          svcId: s.id,
          ranges,
          reservations: list,
          turn: serviceCfg.turn,
          fallback: { start: s.start, end: s.end },
        }),
      ];
    }),
  ) as Record<ServiceId, ServiceWindow>;
  const win = windows[svcId];

  // Le tab mostrano coperti + conteggio e l'orario reale della finestra.
  const tabs = services.map((s) => {
    const list = dayReservations.filter((r) => r.service === s.id && r.status !== 'cancel');
    const w = windows[s.id];
    return {
      ...s,
      time: `${clockLabel(w.start)} – ${clockLabel(w.end)}`,
      count: list.length,
      covers: list.reduce((a, r) => a + r.covers, 0),
    };
  });

  // Linea "adesso": solo se il giorno mostrato è oggi e cade nella finestra.
  const nowSvcMin = NOW_MIN < win.start ? NOW_MIN + 1440 : NOW_MIN;
  const nowPct =
    day === TODAY ? ((nowSvcMin - win.start) / win.span) * 100 : null;

  return (
    <div className="cal">
      <div className="cal-services" role="tablist" aria-label="Service">
        {tabs.map((s) => (
          <button
            key={s.id}
            type="button"
            className={'svc-tab' + (s.id === svcId ? ' on' : '')}
            aria-pressed={s.id === svcId}
            onClick={() => setSvcId(s.id)}
          >
            <span className="svc-name">{s.name[lang]}</span>
            <span className="svc-time">{s.time}</span>
            <span className="svc-cov">
              {s.covers} {t('covers', lang)} · {s.count}
            </span>
          </button>
        ))}
      </div>

      <div className="cal-toolbar">
        <div className="hint">
          <b style={{ fontFamily: 'var(--display)', fontWeight: 600, color: 'var(--ink)' }}>
            {covers}
          </b>{' '}
          {t('covers', lang)} · {live.length}{' '}
          {live.length === 1 ? t('request', lang) : t('requests', lang)}
        </div>
        <div className="legend" aria-hidden="true">
          {(['new', 'conf', 'seated', 'done'] as const).map((k) => (
            <span key={k} className="legend-item">
              <span className="dot" style={{ background: `var(--st-${k})` }} />
              {t(STATUS[k].key, lang)}
            </span>
          ))}
        </div>
      </div>

      {view === 'timeline' && (
        <Timeline
          lang={lang}
          win={win}
          all={all}
          nowPct={nowPct}
          onOpen={onOpen}
          zones={zones}
          tables={tables}
        />
      )}
      {view === 'agenda' && <Agenda lang={lang} live={live} winStart={win.start} onOpen={onOpen} />}
      {view === 'floor' && (
        <Floor lang={lang} all={all} winStart={win.start} onOpen={onOpen} zones={zones} tables={tables} />
      )}
    </div>
  );
}

// ── TIMELINE ─────────────────────────────────────────────────────────────────
interface TimelineProps {
  lang: Lang;
  win: ServiceWindow;
  all: Reservation[];
  nowPct: number | null;
  onOpen: (r: Reservation) => void;
  zones: Zone[];
  tables: Table[];
}

function Timeline({ lang, win, all, nowPct, onOpen, zones, tables }: TimelineProps) {
  const { start: svcStart, span, cols } = win;
  // Bookings without a table assignment (e.g. came in from the public
  // booking portal). The Timeline groups by table, so without this
  // synthetic row they'd be invisible here. Owner can click one to
  // assign a table from the drawer.
  const unassigned = all.filter((r) => !r.table);

  // --cols guida le righe di sfondo della track; la min-width fa scorrere
  // la griglia quando le colonne sono molte (cena oltre mezzanotte).
  const tlStyle = {
    '--cols': cols.length,
    minWidth: `${Math.max(920, 150 + cols.length * 92)}px`,
  } as React.CSSProperties;

  return (
    <div className="tl-wrap">
      <div className="tl" style={tlStyle}>
        <div className="tl-corner">{t('table', lang)}</div>
        <div className="tl-times">
          {cols.map((c, i) => (
            <div key={i} className="tl-time tnum">
              {c}
            </div>
          ))}
        </div>

        {unassigned.length > 0 && (
          <Fragment>
            <div className="tl-zonehead">
              {lang === 'it' ? 'Senza tavolo' : 'Unassigned'}
            </div>
            <div className="tl-rowlabel">
              <span className="tname" style={{ color: 'var(--mute)' }}>—</span>
              <span className="tseats">
                <Icon name="users" size={12} />
                {unassigned.reduce((sum, r) => sum + r.covers, 0)}
              </span>
            </div>
            <div className="tl-track">
              {unassigned.map((r) => {
                const left = ((svcMinute(r.start, svcStart) - svcStart) / span) * 100;
                const width = (r.dur / span) * 100;
                const cls = STATUS[r.status].cls;
                return (
                  <button
                    key={r.id}
                    type="button"
                    className={'resv ' + cls}
                    style={{ left: `${left}%`, width: `calc(${width}% - 4px)` }}
                    onClick={() => onOpen(r)}
                    title={r.name}
                    aria-label={`${r.name}, ${r.covers} ${t('covers', lang)}, ${r.start}`}
                  >
                    <span className="r-name">{r.name}</span>
                    <span className="r-meta tnum">
                      {r.covers} · {r.start}
                    </span>
                  </button>
                );
              })}
              {nowPct !== null && nowPct >= 0 && nowPct <= 100 && (
                <div
                  className="tl-now"
                  style={{ left: `${nowPct}%` }}
                  aria-hidden="true"
                />
              )}
            </div>
          </Fragment>
        )}

        {zones.map((zone) => {
          const tbs = tables.filter((tb) => tb.zone === zone.id);
          return (
            <Fragment key={zone.id}>
              <div className="tl-zonehead">{zone.name[lang]}</div>
              {tbs.map((tb) => {
                const rs = all.filter((r) => r.table === tb.id);
                return (
                  <Fragment key={tb.id}>
                    <div className="tl-rowlabel">
                      <span className="tname">{tb.id}</span>
                      <span className="tseats">
                        <Icon name="users" size={12} />
                        {tb.seats}
                      </span>
                    </div>
                    <div className="tl-track">
                      {rs.map((r) => {
                        const left = ((svcMinute(r.start, svcStart) - svcStart) / span) * 100;
                        const width = (r.dur / span) * 100;
                        const cls = STATUS[r.status].cls;
                        return (
                          <button
                            key={r.id}
                            type="button"
                            className={'resv ' + cls}
                            style={{ left: `${left}%`, width: `calc(${width}% - 4px)` }}
                            onClick={() => onOpen(r)}
                            title={r.name}
                            aria-label={`${r.name}, ${r.covers} ${t('covers', lang)}, ${r.start}`}
                          >
                            <span className="r-name">{r.name}</span>
                            <span className="r-meta tnum">
                              {r.covers} · {r.start}
                            </span>
                          </button>
                        );
                      })}
                      {nowPct !== null && nowPct >= 0 && nowPct <= 100 && (
                        <div
                          className="tl-now"
                          style={{ left: `${nowPct}%` }}
                          aria-hidden="true"
                        />
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ── AGENDA ───────────────────────────────────────────────────────────────────
interface AgendaProps {
  lang: Lang;
  live: Reservation[];
  /** Inizio finestra del servizio: per ordinare il dopo-mezzanotte in fondo. */
  winStart: number;
  onOpen: (r: Reservation) => void;
}

function Agenda({ lang, live, winStart, onOpen }: AgendaProps) {
  const sorted = [...live].sort(
    (a, b) => svcMinute(a.start, winStart) - svcMinute(b.start, winStart),
  );
  const groups: { time: string; items: Reservation[] }[] = [];
  sorted.forEach((r) => {
    const g = groups.find((x) => x.time === r.start);
    if (g) g.items.push(r);
    else groups.push({ time: r.start, items: [r] });
  });

  return (
    <div className="view">
      <div className="agenda">
        {groups.map((g) => (
          <div className="ag-slot" key={g.time}>
            <div className="ag-time tnum">{g.time}</div>
            <div className="ag-list">
              {g.items.map((r) => (
                <button
                  type="button"
                  className="ag-card"
                  key={r.id}
                  onClick={() => onOpen(r)}
                  style={{ width: '100%' }}
                >
                  <div className="ag-cov tnum">{r.covers}</div>
                  <div className="ag-body" style={{ textAlign: 'left' }}>
                    <div className="ag-name">
                      {r.name}
                      <Tags tags={r.tags} lang={lang} max={1} />
                    </div>
                    <div className="ag-info">
                      <span>
                        {t('table', lang)} {r.table}
                      </span>
                      <span>·</span>
                      <span>
                        {r.covers} {t('covers', lang)}
                      </span>
                      <span>·</span>
                      <span className="src">{t(`src_${r.source}`, lang)}</span>
                    </div>
                  </div>
                  <Pill status={r.status} lang={lang} />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── FLOOR PLAN ───────────────────────────────────────────────────────────────
interface FloorProps {
  lang: Lang;
  all: Reservation[];
  winStart: number;
  onOpen: (r: Reservation) => void;
  zones: Zone[];
  tables: Table[];
}

function Floor({ lang, all, winStart, onOpen, zones, tables }: FloorProps) {
  return (
    <div className="view">
      <div className="floor">
        <div className="floor-zones">
          {zones.map((zone) => {
            const tbs = tables.filter((tb) => tb.zone === zone.id);
            return (
              <div className="floor-zone" key={zone.id}>
                <h3>{zone.name[lang]}</h3>
                <div className="floor-grid">
                  {tbs.map((tb) => {
                    const rs = all
                      .filter((r) => r.table === tb.id && r.status !== 'cancel')
                      .sort((a, b) => svcMinute(a.start, winStart) - svcMinute(b.start, winStart));
                    const cur = rs.find((r) => r.status === 'seated') ?? rs[0];
                    const occ = cur ? ` occ-${STATUS[cur.status].cls}` : '';
                    const isOccupied = !!cur;
                    return (
                      <button
                        key={tb.id}
                        type="button"
                        className={'tbl' + (isOccupied ? occ : ' free')}
                        onClick={() => isOccupied && onOpen(cur!)}
                        aria-label={
                          isOccupied
                            ? `${tb.id} — ${cur!.name}, ${cur!.start}, ${cur!.covers} ${t('covers', lang)}`
                            : `${tb.id} ${t('free', lang)}`
                        }
                      >
                        <span className="tbl-band" aria-hidden="true" />
                        <span className="tbl-id">{tb.id}</span>
                        <span className="tbl-seats">
                          <Icon name="users" size={11} />
                          {tb.seats} {t('seats', lang)}
                        </span>
                        {isOccupied ? (
                          <>
                            <span className="tbl-guest">{cur!.name}</span>
                            <span className="tbl-when tnum">
                              {cur!.start} · {cur!.covers} {t('covers', lang)}
                            </span>
                          </>
                        ) : (
                          <span className="tbl-guest">{t('free', lang)}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
